import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Coffee, Menu, X, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const TransparentHeader = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { brand } = useBrand();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getDashboardRoute = () => {
    if (!user?.role) {
      return '/dashboard';
    }
    
    switch (user.role) {
      case 'OWNER':
        return '/dashboard';
      case 'BARISTA':
        return '/barista';
      case 'ADMIN':
        return '/admin';
      default:
        return '/dashboard';
    }
  };

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.posts'), path: '/posts' },
    { name: t('nav.pricing'), path: '/pricing' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-black/80 backdrop-blur-lg border-b border-white/10 shadow-2xl' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group"
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            {brand?.logoUrl ? (
              <img 
                src={brand.logoUrl} 
                alt={brand?.name || 'CoffeeCloud'} 
                className="group-hover:scale-110 transition-transform duration-300"
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  objectFit: 'contain',
                  maxWidth: '40px',
                  maxHeight: '40px',
                  display: 'block'
                }}
              />
            ) : (
              <div className="p-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 group-hover:from-orange-600 group-hover:to-orange-700 transition-all duration-300 group-hover:scale-110 shadow-lg">
                <Coffee className="h-6 w-6 text-white" />
              </div>
            )}
            <span className="text-2xl font-black text-white group-hover:text-orange-300 transition-colors duration-300">
              {brand?.name || 'CoffeeCloud'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative font-medium transition-all duration-300 hover:text-orange-300 group ${
                  location.pathname === item.path 
                    ? 'text-orange-400' 
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-orange-600 group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link
                  to={getDashboardRoute()}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  {t('landing.hero.myDashboard')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 hover:scale-110"
                  title={t('landing.hero.logout')}
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="px-6 py-3 text-white font-medium hover:text-orange-300 transition-all duration-300 hover:scale-105"
                >
                  {t('landing.hero.signIn')}
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  {t('landing.hero.startTrial')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-3 text-white hover:bg-white/10 rounded-xl transition-all duration-300"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-white/10 shadow-2xl">
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                    location.pathname === item.path
                      ? 'text-orange-400 bg-orange-500/10'
                      : 'text-white hover:text-orange-300 hover:bg-white/5'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              <div className="pt-4 border-t border-white/10 space-y-4">
                {isAuthenticated ? (
                  <>
                    <Link
                      to={getDashboardRoute()}
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl text-center transition-all duration-300"
                    >
                      {t('landing.hero.myDashboard')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="w-full py-3 px-4 text-white hover:text-orange-300 font-medium rounded-xl text-center transition-all duration-300 hover:bg-white/5"
                    >
                      {t('landing.hero.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 px-4 text-white hover:text-orange-300 font-medium rounded-xl text-center transition-all duration-300 hover:bg-white/5"
                    >
                      {t('landing.hero.signIn')}
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl text-center transition-all duration-300"
                    >
                      {t('landing.hero.startTrial')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default TransparentHeader;
